import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from 'src/environments/environment';
import {DavinciLanguageService} from '../davinci-services/davinci-language.service';
import {DavinciLoadWidgetService, DaVinciSuccessResponse} from '../davinci-services/davinci-load-widget.service';
import {SsoService} from '../../authentication/services/sso.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'app-davinci-login',
  templateUrl: './davinci-login.component.html',
  styleUrls: ['./davinci-login.component.scss'],
})
export class DavinciLoginComponent implements OnInit {
  @ViewChild('davinciWidgetContainer', { static: true })
  widgetContainer!: ElementRef<HTMLDivElement>;

  isLoading = true;
  errorMessage = '';

  flowAction: string | null = null;

  constructor(
    private languageService: DavinciLanguageService,
    private davinciService: DavinciLoadWidgetService,
    private route: ActivatedRoute,
    private router: Router,
    private ssoService: SsoService,
    private cookie: CookieService
  ) {}

  ngOnInit(): void {
    // NEW: Read the flow input from router data
    this.route.data.subscribe(data => {
      this.flowAction = data.flowAction || 'login';
    });

    // Step 1: Check if language state is synchronized.
    // The service will update the URL if needed and tell us if a reload is required.
    if (this.languageService.synchronizeAndCheckForReload()) {

      // A reload is needed because the language state was out of sync.
      // The service has already triggered a URL update. Now, we reload the page.
      // The timeout gives the browser a moment to process the URL change before the reload.
      setTimeout(() => window.location.reload(), 50);

      // Halt further execution of this component instance, as the page is about to reload.
      return;
    }

    // Step 2: Language state is correct. Render DaVinci flow.
    console.log('Language synchronized. Rendering DaVinci flow.');

    const policyId = environment.ping_login_flow_policyId;

    this.davinciService.renderFlow({
      policyId,
      targetElement: this.widgetContainer.nativeElement,
      flowInputData: { flowAction: this.flowAction },
      onReady: () => {
        this.isLoading = false;
      }
    }).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.handleSuccess(response);
      },
      error: (err) => {
        this.isLoading = false;
        this.handleError(err);
      }
    });
  }

  /**
   * Handles a successful login response from the DaVinci flow.
   * @param response The success response containing the access token.
   */
  private handleSuccess(response: DaVinciSuccessResponse): void {
    const currentUnixTime = Math.floor(Date.now() / 1000);
    console.log('response: ', response);

    localStorage.setItem('ping-access-token', response.access_token);
    localStorage.setItem('ping-refresh-token', response.refresh_token);

    // Needed for idp sso - PHP login auto clicking
    this.cookie.set('signedIn', 'true', null, '/');
    this.cookie.set('sessiat', currentUnixTime.toString());

    localStorage.setItem('signedIn', 'true');
    localStorage.setItem('sessiat', currentUnixTime.toString());

    const manageAccountFlag = this.normalizeFlag(
      response &&
      response.additionalProperties &&
      response.additionalProperties.manageAccountFlag
    );

    console.log('manageAccountFlag (normalized):', manageAccountFlag);

    if (manageAccountFlag === 'true') {
      console.log('manage account flag is true, redirecting to /manage-account');
      window.location.assign(`${window.location.origin}/manage-account`);
      return;
    }

    // Migrated old redirectToEndOfFlow()
    this.redirectToEndOfFlow();
  }


  /**
   * Handles errors that occur during the DaVinci flow.
   * @param error The error object.
   */
  private handleError(error: any): void {
    console.error('DaVinci login failed:', error);
    this.router.navigate(['/error']);
  }

  /**
   * Resolves the post-login destination, preferring SSO, then returnUrl, then the default dashboard.
   * Navigates or redirects accordingly and returns true after a redirect attempt.
   */
  private redirectToEndOfFlow(): boolean {
    const params = this.route.snapshot.queryParams;
    const rawRedirectUri = params.redirect_uri;
    const redirectUri = rawRedirectUri ? decodeURIComponent(rawRedirectUri) : null;
    const stateParam = params.state;
    const returnUrl = params.returnUrl;

    try {
      // 1. SSO redirect via /authorize (same as old)
      if (redirectUri && this.ssoService.isSpValid(redirectUri)) {
        this.router.navigate(['/authorize'], {
          queryParams: { redirect_uri: redirectUri, state: stateParam }
        });
        return true;
      }

      // 2. Fallback: returnUrl (same as old)
      if (returnUrl) {
        this.router.navigateByUrl(returnUrl);
        return true;
      }

      // 3. Default: dashboard (same as old)
      window.location.href = environment.vistana_baseurl + '/dashboard';
      return true;
    } catch (err) {
      console.error('Post-login redirect failed', err);
      // 4. Error fallback (same as old)
      this.router.navigate(['/settings/account-summary']);
      return true;
    }
  }

  private normalizeFlag(value: unknown): string {
    if (value == null) {
      return '';
    }
    return String(value)
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // remove zero-width chars
      .trim()
      .toLowerCase();
  }

}
